import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { isNdtLogRejected } from '../../../utils/fetchNdtIndicationLogsApi';
import type { NdtIndicationRow } from './drillDownTypes';
import { NdtIndicationDrillDownHost } from './NdtIndicationDrillDownHost';

interface NdtIndicationSummaryProps {
  logs: NdtIndicationRow[];
  loading?: boolean;
  error?: string | null;
  weldNumber?: string;
  jobNumber?: string;
}

export const NdtIndicationSummary = ({
  logs,
  loading,
  error,
  weldNumber = '',
  jobNumber = '',
}: NdtIndicationSummaryProps) => {
  const [open, setOpen] = useState(false);

  if (loading) {
    return <p className="text-sm text-heritage-grey">Loading NDT indication logs…</p>;
  }
  if (error) {
    return <p className="text-sm text-readable-red">{error}</p>;
  }
  if (!logs.length) {
    return <p className="text-sm text-heritage-grey">No NDT indication logs for this weld.</p>;
  }

  const rejectedCount = logs.filter((log) => isNdtLogRejected(log)).length;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E2E5EA] bg-white px-3 py-3">
        <div>
          <p className="text-sm font-medium text-blueprint-blue">
            {logs.length} indication{logs.length === 1 ? '' : 's'}
          </p>
          <p className="text-xs text-heritage-grey">
            {rejectedCount} rejected · {logs.length - rejectedCount} other — open for the full NDT record
          </p>
        </div>
        <button
          type="button"
          data-testid="open-ndt-indications"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 text-xs font-semibold text-blueprint-blue hover:border-blueprint-blue"
        >
          Open NDT indications
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>
      <NdtIndicationDrillDownHost
        open={open}
        onOpenChange={setOpen}
        weldNumber={weldNumber}
        jobNumber={jobNumber}
        logs={logs}
      />
    </div>
  );
};
